import React, {useCallback, useState} from 'react';
import {
    AppBar,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import styled from '@emotion/styled';
import {Link} from "react-router-dom";
import {useNavigate} from "react-router";
import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import {useAuth} from "../providers/AuthProvider";
import {logError} from "../utils/logger";
import {ROUTES} from "../constants";
import ErrorBoundary from "../components/ErrorBoundary";
import {useFeatureAccess} from "../hooks/useFeatureAccess";
import { useAnalytics } from '../context/AnalyticsContext';
import { NavigationEvents } from '../services/analytics/events';
import { useDemoMode } from '../providers/DemoModeProvider';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import SoloSocialLogo from '../components/SoloSocialLogo';


const SkipLink = styled('a')(({theme}) => ({
    position: 'absolute',
    left: '-999px',
    width: '1px',
    height: '1px',
    top: 'auto',
    overflow: 'hidden',
    zIndex: 1500,
    padding: theme.spacing(1),
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    textDecoration: 'none',
    '&:focus': {
        left: theme.spacing(2),
        width: 'auto',
        height: 'auto',
        overflow: 'visible',
        outline: `2px solid ${theme.palette.primary.main}`,
    },
}));

const StyledButton = styled(Button)(({theme}) => ({
    marginRight: theme.spacing(2),
}));

const MobileMenu = styled('div')(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    transition: 'background-color 0.2s',
    padding: theme.spacing(0.5),
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const BetaTag = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    padding: '2px 6px',
    fontSize: '0.625rem',
    fontWeight: 600,
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    borderRadius: theme.spacing(0.5),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    lineHeight: 1,
}));

function TopNavBar({ onSideNavToggle, sidebarCollapsed, isMobile: isMobileProp, isAuthenticated: isAuthenticatedProp }) {
    const {isAuthenticated, logout} = useAuth();
    const { canAccessTemplateEditor, canCreatePosts } = useFeatureAccess();
    const { isDemoMode, toggleDemoMode } = useDemoMode();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
    const { trackEvent } = useAnalytics();

    const handleClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
        trackEvent(NavigationEvents.NAV_PROFILE_CLICK);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleMobileMenuOpen = useCallback((event) => {
        setMobileMenuAnchorEl(event.currentTarget);
        trackEvent(NavigationEvents.MOBILE_MENU_OPEN);
    }, [trackEvent]);

    const handleMobileMenuClose = useCallback(() => {
        setMobileMenuAnchorEl(null);
        trackEvent(NavigationEvents.MOBILE_MENU_CLOSE);
    }, [trackEvent]);

    const handleLogout = useCallback((event) => {
        event.preventDefault();
        try {
            logout();
            navigate(ROUTES.HOME, {replace: true});
        } catch (error) {
            logError('Logout error', error);
        }
    }, [logout, navigate, trackEvent]);

    const handleNavigation = useCallback((route, eventType) => {
        trackEvent(eventType);
        navigate(route);
    }, [navigate, trackEvent]);

    const handleLogoClick = useCallback(() => {
        const targetRoute = isAuthenticated ? ROUTES.DASHBOARD : ROUTES.HOME;
        trackEvent(NavigationEvents.NAV_LOGO_CLICK);
        navigate(targetRoute);
    }, [isAuthenticated, navigate, trackEvent]);

    // Determine if branding should be shown
    // Show branding when: authenticated user has collapsed sidebar on desktop, or user is not authenticated
    const shouldShowBranding = !isAuthenticated || (isAuthenticated && sidebarCollapsed && !isMobile);

    const renderBranding = () => {
        if (!shouldShowBranding) return null;
        
        return (
            <LogoContainer 
                onClick={handleLogoClick}
                aria-label="Solo Social - Go to dashboard"
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleLogoClick();
                    }
                }}
            >
                <SoloSocialLogo 
                    sx={{ 
                        width: { xs: 28, sm: 32 }, 
                        height: { xs: 28, sm: 32 },
                        color: 'primary.main'
                    }} 
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography 
                        variant="h6" 
                        component="span" 
                        sx={{ 
                            fontWeight: 700,
                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                            display: { xs: 'none', sm: 'block' }
                        }}
                    >
                        Solo Social
                    </Typography>
                    <BetaTag>
                        Beta
                    </BetaTag>
                </Box>
            </LogoContainer>
        );
    };

    const renderMenu = () => (
        <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center">
            {isAuthenticated ? (
                <>
                    {/* Demo Mode Toggle */}
                    <Tooltip title={isDemoMode ? "Using demo data for all pages" : "Using real API data"}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isDemoMode}
                                    onChange={toggleDemoMode}
                                    size="small"
                                    color="primary"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
                                    Demo Mode
                                    {isDemoMode && (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 0.5,
                                                px: 0.75,
                                                py: 0.25,
                                                fontSize: '0.625rem',
                                                fontWeight: 600,
                                                backgroundColor: 'warning.main',
                                                color: 'warning.contrastText',
                                                borderRadius: 0.5,
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            ON
                                        </Box>
                                    )}
                                </Box>
                            }
                            sx={{ 
                                mr: 2, 
                                mb: isMobile ? 1 : 0,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                }
                            }}
                        />
                    </Tooltip>
                    
                    {canAccessTemplateEditor && (
                        <StyledButton variant="outlined" component={Link} to={ROUTES.EDITOR} onClick={() => handleNavigation(ROUTES.EDITOR, NavigationEvents.NAV_TEMPLATES_CLICK)}>
                            Editor
                        </StyledButton>
                    )}
                    {canCreatePosts && (
                        <StyledButton variant="contained" component={Link} to={ROUTES.CREATE_POST} onClick={() => handleNavigation(ROUTES.CREATE_POST, NavigationEvents.NAV_POSTS_CLICK)}>
                            Create a Post
                        </StyledButton>
                    )}
                    {!isMobile && (
                        <IconButton
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleClick}
                            color="inherit"
                        >
                            <AccountCircle/>
                        </IconButton>
                    )}
                </>
            ) : (
                <>
                    <Button component={Link} to={ROUTES.LOGIN} color="inherit" onClick={() => handleNavigation(ROUTES.LOGIN, NavigationEvents.NAV_LOGIN_CLICK)}>Sign In</Button>
                    <Button variant="outlined" component={Link} to={ROUTES.SIGNUP} color="primary" onClick={() => handleNavigation(ROUTES.SIGNUP, NavigationEvents.NAV_SIGNUP_CLICK)}>Join Now</Button>
                </>
            )}
        </Stack>
    );

    return (
        <ErrorBoundary>
            <SkipLink href="#main-content">
                Skip to main content
            </SkipLink>
            <AppBar 
                elevation={0} 
                position="fixed" 
                color="inherit"
                sx={{ 
                    zIndex: (theme) => theme.zIndex.drawer - 1, // Ensure TopNavBar is behind SideNavBar
                    // Add left margin when sidebar is expanded and user is authenticated
                    ml: isAuthenticated && !sidebarCollapsed && !isMobile ? '280px' : 0,
                    width: isAuthenticated && !sidebarCollapsed && !isMobile ? 'calc(100% - 280px)' : '100%',
                    transition: (theme) => theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                <Toolbar 
                    component="nav" 
                    aria-label="Main navigation"
                    sx={{
                        px: { xs: 3, md: 6 }, // Match the content padding: 24px base + 24px from AppLayout = 48px
                        maxWidth: 'none',
                        // Add left padding when sidebar is collapsed on desktop to avoid overlap
                        pl: isAuthenticated && sidebarCollapsed && !isMobile ? 
                            { xs: 3, sm: '84px', md: '84px' } : // 72px collapsed sidebar + 12px padding
                            { xs: 3, md: 6 },
                        transition: (theme) => theme.transitions.create('padding', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                    }}
                >
                        {isAuthenticated && isMobile && (
                            <IconButton
                                size="large"
                                edge="start"
                                color="inherit"
                                aria-label="open navigation drawer"
                                onClick={onSideNavToggle}
                                sx={{ mr: 2 }}
                            >
                                <MenuIcon/>
                            </IconButton>
                        )}
                        
                        {/* Always show branding */}
                        {renderBranding()}
                        
                        {/* Spacer to push menu to the right */}
                        <Box sx={{ flexGrow: 1 }} />
                        {isMobile && !isAuthenticated ? (
                            <IconButton
                                size="large"
                                edge="start"
                                color="inherit"
                                aria-label="menu"
                                onClick={handleMobileMenuOpen}
                            >
                                <MenuIcon/>
                            </IconButton>
                        ) : !isMobile ? (
                            renderMenu()
                        ) : null}
                </Toolbar>
            </AppBar>
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 2, // Ensure menus are above everything
                }}
            >
                <MenuItem onClick={() => {
                        handleClose();
                        handleNavigation(ROUTES.PROFILE, NavigationEvents.NAV_PROFILE_CLICK);
                    }} component={Link} to={ROUTES.PROFILE}>Profile</MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
            <Menu
                anchorEl={mobileMenuAnchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(mobileMenuAnchorEl)}
                onClose={handleMobileMenuClose}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 2, // Ensure menus are above everything
                }}
            >
                <MobileMenu>
                    {renderMenu()}
                    {isAuthenticated && (
                        <>
                            <MenuItem 
                                onClick={() => {
                                    handleMobileMenuClose();
                                    handleNavigation(ROUTES.PROFILE, NavigationEvents.NAV_PROFILE_CLICK);
                                }}
                                component={Link} 
                                to={ROUTES.PROFILE}
                            >
                                Profile
                            </MenuItem>
                            <MenuItem onClick={handleLogout}>Logout</MenuItem>
                        </>
                    )}
                </MobileMenu>
            </Menu>
        </ErrorBoundary>
    );
}

TopNavBar.propTypes = {
    // Add any props if needed
};

export default React.memo(TopNavBar);